import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { APP_BASE_PATHNAMES } from "../../../../core/consts/consts";
import Container from "../../../../components/container/container";
import MyNexarContext from "../../../../components/my-nexar-context/my-nexar-context";
import PhoneField from "../../components/phone-field";
import OTPScreen from "../otp/otp";
import ErrorScreen from "../error/error";
import Button from "../../../../components/button/button";
import validatePhone from "../../utils/validate-phone";
import styles from "./login.module.scss";

import Event from "../../../../core/consts/analytics-events";
import NavBar from "../../../../components/nav-bar/nav-bar";
import Modal from "../../../../components/modal/modal";
import { useDI } from "../../../../core/di";

export function Login() {
  const { t } = useTranslation();
  let navigate = useNavigate();
  const { clearLoggedInUser } = useContext(MyNexarContext);
  const [error, setError] = useState<string | undefined>();
  const [showOtpScreen, setShowOtpScreen] = useState<boolean>();
  const [searchParams] = useSearchParams();
  const { analytics, nexarApi, logger } = useDI();

  useEffect(() => {
    analytics?.track(Event.LoginViewed);
  }, []);

  const formik = useFormik({
    initialValues: {
      phone: "",
    },
    validateOnChange: true,
    onSubmit: (values) => {
      analytics?.track(Event.LoginLoginClicked, { phoneNumber: values.phone });
      setShowOtpScreen(true);
    },
    validate: (values) => {
      const errors = validatePhone(values.phone);
      if (errors.phone) {
        analytics?.track(Event.LoginErrorViewed, { phoneNumber: values.phone });
      }
      return errors;
    },
  });

  const renderForm = () => {
    const handlePrivacyClick = () => {
      analytics?.track(Event.LoginPrivacyPolicyClicked);
    };
    const handleTermsClick = () => {
      analytics?.track(Event.LoginTermsofServiceClicked);
    };
    const handleFormNavBack = () => {
      analytics?.track(Event.LoginBackClicked);
    };

    const acknowledgmentParagraph = (
      <p className={styles.P}>
        I acknowledge that my data will be processed in accordance with{" "}
        <a
          data-testid={"link-privacy"}
          href="https://www.getnexar.com/privacy/"
          target="_blank"
          rel="noreferrer"
          onClick={handlePrivacyClick}
        >
          Nexar’s Privacy Policy
        </a>
        . I agree to use the services according to{" "}
        <a
          data-testid={"link-terms"}
          href="https://www.getnexar.com/terms/"
          target="_blank"
          rel="noreferrer"
          onClick={handleTermsClick}
        >
          Nexar’s Terms of Service
        </a>
        .
      </p>
    );

    return (
      <Modal
        data-testid={"login"}
        title={t("login")}
        navBar={<NavBar to="/" onClick={handleFormNavBack} mode={"back"} />}
      >
        <form onSubmit={formik.handleSubmit} className={styles.FormWrapper}>
          <PhoneField
            inputProps={{
              name: "phone",
              defaultValue: formik.values.phone,
              role: "input",
            }}
            onChange={formik.handleChange}
            helperText={formik.errors?.phone || ""}
            error={!!formik.errors.phone}
            onBlur={() => {}}
          />
          <footer>
            {acknowledgmentParagraph}
            <Button
              data-testid={"submit-field"}
              disabled={!!formik.errors?.phone || !formik.values.phone}
              submit={true}
            >
              {t("login")}
            </Button>
          </footer>
        </form>
      </Modal>
    );
  };

  const done = async () => {
    const loginTarget =
      searchParams.get("redirect") || `/${APP_BASE_PATHNAMES.CONNECT}`;
    navigate(loginTarget);
  };

  const closeOtpScreen = () => {
    setShowOtpScreen(false);
  };
  const closeErrorScreen = () => {
    clearLoggedInUser();
    setError(undefined);
    closeOtpScreen();
  };

  const getComponent = () => {
    if (error) {
      return <ErrorScreen error={error} onClose={closeErrorScreen} />;
    }
    if (showOtpScreen && formik.values.phone) {
      return (
        <OTPScreen
          onSuccess={done}
          phone={formik.values.phone}
          onClose={closeOtpScreen}
        />
      );
    } else {
      return renderForm();
    }
  };

  return <Container fullScreen>{getComponent()}</Container>;
}

export default Login;
